import React, { useState } from 'react';

interface GridCardProps {
  imageSrc: string;
  imageAlt: string;
  title: string;
  description?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  imageClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  children?: React.ReactNode;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

// Flat card (not relying on Compass's Card) so that all Tailwind classes
// passed via props apply without fighting the design system's internal styles/inline styles.
const GridCard: React.FC<GridCardProps> = ({
  imageSrc,
  imageAlt,
  title,
  description,
  onClick,
  disabled = false,
  className,
  imageClassName,
  contentClassName,
  titleClassName,
  descriptionClassName,
  children,
}) => {
  // Remembers which src failed, so a new src gets a fresh try.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showPlaceholder = !imageSrc || failedSrc === imageSrc;

  const cardClassName = cx(
    'relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-transparent shadow-sm',
    disabled ? 'opacity-40 pointer-events-none' : 'cursor-pointer',
    className
  );

  const content = (
    <>
      {showPlaceholder ? (
        <div
          role="img"
          aria-label={imageAlt}
          className={cx('w-full h-32 bg-gray-100 rounded-t-2xl rounded-b-none', imageClassName)}
          data-testid="grid-card-placeholder"
        />
      ) : (
        <img
          src={imageSrc}
          alt={imageAlt}
          onError={() => setFailedSrc(imageSrc)}
          className={cx('w-full h-32 object-cover rounded-t-2xl rounded-b-none', imageClassName)}
        />
      )}
      <div className={cx('flex flex-col gap-1 p-3', contentClassName)}>
        <h3 className={cx('text-base font-bold text-gray-900', titleClassName)}>{title}</h3>
        {description && (
          <p className={cx('text-sm text-gray-500', descriptionClassName)}>{description}</p>
        )}
      </div>
      {children}
    </>
  );

  if (onClick) {
    return (
      <button type="button" disabled={disabled} onClick={onClick} className={cx('w-full text-left', cardClassName)}>
        {content}
      </button>
    );
  }

  return <div className={cardClassName}>{content}</div>;
};

export default GridCard;
